import { motion } from "framer-motion";
import { Zap, TimerReset, BadgeDollarSign, ArrowRight } from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";
import { REGISTER_URL } from "@/data/plans";

const CARDS = [
  {
    icon: Zap,
    title: "Instant Maximum Allocation",
    desc: "Access up to $400,000 in total funded capital from Day 1 — across multiple accounts running side by side. Three $100K challenges, two $50K, eight $50K instant accounts — combine them your way.",
  },
  {
    icon: TimerReset,
    title: "No Holding Periods. No Reviews.",
    desc: "We scrapped quarterly scaling reviews and performance milestones. Pass your challenge, get funded, trade. Your capital never waits on a calendar.",
  },
  {
    icon: BadgeDollarSign,
    title: "100% Fee Refund",
    desc: "Your one-time challenge fee comes back to you in full with your first payout. Funded trading that pays for itself.",
  },
];

export default function ScalingPlan() {
  return (
    <section id="scaling" className="py-24 lg:py-32 bg-[#030B16]/40 relative" data-testid="scaling-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center flex flex-col items-center">
          <div className="flex items-center gap-4 mb-6">
            <span className="eyebrow">06</span>
            <span className="h-px w-10 bg-gradient-to-r from-[#FFD700] to-transparent" />
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#999999]">Maximum Capital</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.04em] text-white">
            Full capital. <span className="gold-text">Day one.</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#999999] max-w-2xl">
            Most firms make you scale up over months. We don't. Buy and pass as many challenges as
            you like and hold up to $400,000 in funded capital immediately — no waiting periods,
            no scaling reviews, no milestones between you and maximum allocation.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {CARDS.map((c, i) => (
            <motion.div
              key={c.title}
              data-testid={`allocation-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="glass-card rounded-2xl p-7 h-full group"
            >
              <div className="w-12 h-12 rounded-xl bg-[#FFD700]/10 border border-[#FFD700]/25 flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110">
                <c.icon size={22} className="text-[#FFD700]" />
              </div>
              <h3 className="text-lg font-semibold tracking-[-0.02em] text-white">{c.title}</h3>
              <p className="text-sm text-[#999999] mt-2.5 leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-12 text-center">
          <a
            href={REGISTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="scaling-cta-button"
            className="btn-gold group inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-bold"
          >
            Claim Your Full Capital
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </div>
    </section>
  );
}
